import { useState, type ChangeEvent, type FormEvent } from 'react';
import { collection, query } from 'firebase/firestore';
import { callable, db } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { useDocs } from '@/lib/useDocs';
import { messageForAuthError } from '@/lib/authErrors';
import { strings } from '@/strings';

const registerOrganization = callable<Record<string, unknown>, { orgId: string; status: string }>('registerOrganization');

const SIZES = ['1-10', '11-50', '51-200', '201+'];

type CauseAreasDoc = { areas?: Array<{ id: string; nameAr: string }> };

const EMPTY = {
  name: '', city: '', mission: '', size: '', foundedYear: '',
  contactName: '', contactTitle: '', crNumber: '', licenseNumber: '',
};

/**
 * The only self-service entry point in Areeb. It states up front that approval
 * gates publishing rather than access, because otherwise the pending status
 * that follows reads as "you are locked out" and people stop here.
 */
export function RegisterOrgPage() {
  const { refreshRole } = useAuth();
  const { rows } = useDocs<CauseAreasDoc>(query(collection(db, 'config')), []);
  const causes = rows.find((r) => r.id === 'causeAreas')?.areas || [];

  const [form, setForm] = useState(EMPTY);
  const [causeTags, setCauseTags] = useState<string[]>([]);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const ready = form.name.trim() !== '' && form.city.trim() !== '' && form.mission.trim() !== ''
    && SIZES.includes(form.size) && form.contactName.trim() !== '' && form.contactTitle.trim() !== '';

  function set(field: keyof typeof EMPTY) {
    return (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
      setForm((prev) => ({ ...prev, [field]: e.target.value }));
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (!ready || busy) return;
    setBusy(true);
    setError(null);
    try {
      await registerOrganization({
        name: form.name.trim(), city: form.city.trim(), mission: form.mission.trim(),
        size: form.size, foundedYear: Number(form.foundedYear) || 0, causeTags,
        contactName: form.contactName.trim(), contactTitle: form.contactTitle.trim(),
        crNumber: form.crNumber.trim(), licenseNumber: form.licenseNumber.trim(),
      });
      // The role changed server-side; without a token refresh this client
      // still believes it is a guest and the router keeps it on /welcome.
      await refreshRole();
      setDone(true);
    } catch (err) {
      setError(messageForAuthError((err as Error).message));
    } finally {
      setBusy(false);
    }
  }

  if (done) {
    return (
      <div className="flex max-w-lg flex-col gap-6">
        <h1 className="text-xl font-bold text-primary">{strings.registerOrgTitle}</h1>
        <p className="text-success">{strings.registerOrgDone}</p>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="flex max-w-lg flex-col gap-6">
      <h1 className="text-xl font-bold text-primary">{strings.registerOrgTitle}</h1>
      <p className="text-sm text-muted">{strings.registerOrgIntro}</p>

      {([
        ['name', strings.orgName], ['city', strings.orgCity],
        ['contactName', strings.orgContactName], ['contactTitle', strings.orgContactTitle],
        ['crNumber', strings.orgCrNumber], ['licenseNumber', strings.orgLicense],
      ] as const).map(([field, label]) => (
        <label key={field} className="flex flex-col gap-1 text-sm" htmlFor={field}>
          {label}
          <input id={field} value={form[field]} onChange={set(field)}
            className="rounded-md border border-border-subtle px-3 py-2" />
        </label>
      ))}

      <label className="flex flex-col gap-1 text-sm" htmlFor="mission">
        {strings.orgMission}
        <textarea id="mission" rows={2} value={form.mission} onChange={set('mission')}
          className="rounded-md border border-border-subtle px-3 py-2" />
      </label>

      <label className="flex flex-col gap-1 text-sm" htmlFor="size">
        {strings.orgSize}
        <select id="size" value={form.size} onChange={set('size')}
          className="rounded-md border border-border-subtle px-3 py-2">
          <option value="" />
          {SIZES.map((size) => <option key={size} value={size}>{size}</option>)}
        </select>
      </label>

      <label className="flex flex-col gap-1 text-sm" htmlFor="foundedYear">
        {strings.orgFounded}
        <input id="foundedYear" type="number" value={form.foundedYear} onChange={set('foundedYear')}
          className="rounded-md border border-border-subtle px-3 py-2" />
      </label>

      <fieldset className="flex flex-col gap-2">
        <legend className="text-sm">{strings.orgCauses}</legend>
        <div className="flex flex-wrap gap-3">
          {causes.map((cause) => (
            <label key={cause.id} className="flex items-center gap-1 text-sm" htmlFor={`cause-${cause.id}`}>
              <input
                id={`cause-${cause.id}`}
                type="checkbox"
                checked={causeTags.includes(cause.id)}
                onChange={(e) => setCauseTags((prev) =>
                  e.target.checked ? [...prev, cause.id] : prev.filter((c) => c !== cause.id))}
              />
              {cause.nameAr}
            </label>
          ))}
        </div>
      </fieldset>

      <button type="submit" disabled={!ready || busy}
        className="min-h-11 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary-dark disabled:cursor-not-allowed disabled:opacity-50">
        {strings.registerOrgSubmit}
      </button>
      {error ? <p role="alert" className="text-sm text-danger">{error}</p> : null}
    </form>
  );
}
